import { useMemo, useState } from 'react';
import { Minimize2, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import {
  formatSql,
  minifySql,
  SQL_DIALECTS,
  SQL_DIALECT_LABEL,
  type KeywordCase,
  type SqlDialect,
  type SqlResult,
} from '../lib/sqlFormat';
import { DualPaneFormatter } from '../components/DualPaneFormatter';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/format/sql` — multi-dialect SQL formatter / minifier.
 *
 * Dialect / keyword case / indent are independent controls; format and
 * minify share the dialect (so a `--` comment doesn't get eaten by the
 * wrong lexer). Validation is intentionally out of scope, see lib doc.
 */

type Mode = 'format' | 'minify';

const PLACEHOLDER = `粘贴 SQL…
例如：select id, name, created_at from users where status='active' order by id desc limit 10;`;

export default function SqlTool(): JSX.Element {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>('format');
  const [dialect, setDialect] = useState<SqlDialect>('postgresql');
  const [keywordCase, setKeywordCase] = useState<KeywordCase>('upper');
  const [tabWidth, setTabWidth] = useState<2 | 4>(2);

  const result: SqlResult = useMemo(() => {
    if (mode === 'format') {
      return formatSql(input, { dialect, keywordCase, tabWidth });
    }
    return minifySql(input);
  }, [input, mode, dialect, keywordCase, tabWidth]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-4">
      <ToolHeader
        toolId="sql"
        category="format"
        description="多方言 SQL 美化 / 压缩。支持 PostgreSQL / MySQL / SQL Server (T-SQL) / Oracle (PL/SQL) / SQLite / BigQuery / Snowflake 等十余种方言；关键字大小写可调。"
      />
      <DualPaneFormatter
        language="sql"
        inputValue={input}
        onInputChange={setInput}
        inputPlaceholder={PLACEHOLDER}
        outputValue={result.ok ? result.output : ''}
        outputFilename="output.sql"
        error={result.ok ? undefined : { message: result.error }}
        toolbar={
          <>
            <div className="inline-flex rounded-md border border-line">
              <ModeButton current={mode} value="format" onClick={setMode}>
                <Sparkles className="h-3.5 w-3.5" />
                美化
              </ModeButton>
              <ModeButton current={mode} value="minify" onClick={setMode}>
                <Minimize2 className="h-3.5 w-3.5" />
                压缩
              </ModeButton>
            </div>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <span>方言</span>
              <select
                className="select py-0.5"
                value={dialect}
                onChange={(e) => setDialect(e.target.value as SqlDialect)}
              >
                {SQL_DIALECTS.map((d) => (
                  <option key={d} value={d}>
                    {SQL_DIALECT_LABEL[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <span>关键字</span>
              <select
                className="select py-0.5"
                value={keywordCase}
                onChange={(e) => setKeywordCase(e.target.value as KeywordCase)}
                disabled={mode !== 'format'}
                title="关键字大小写（仅美化模式）"
              >
                <option value="upper">UPPER</option>
                <option value="lower">lower</option>
                <option value="preserve">保留</option>
              </select>
            </label>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <span>缩进</span>
              <select
                className="select py-0.5"
                value={tabWidth}
                onChange={(e) => setTabWidth(Number(e.target.value) as 2 | 4)}
                disabled={mode !== 'format'}
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
              </select>
            </label>
          </>
        }
      />
    </div>
  );
}

function ModeButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Mode;
  value: Mode;
  onClick: (m: Mode) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={clsx(
        'inline-flex items-center gap-1 px-2.5 py-0.5 text-[11.5px] font-medium transition first:rounded-l-md last:rounded-r-md',
        active ? 'bg-accent/10 text-accent' : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
